import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon } from "../components/Icons";
import {
  AnswerField,
  MathKeypad,
  expressionKeysFor,
  numericKeysFor,
} from "../components/MathKeys";
import ui from "../components/ui.module.css";
import { tableCellsWrong } from "../data/activityAdapter";
import useSentBack from "../hooks/useSentBack";
import {
  formatExpression,
  isExpressionValue,
  readExpressionEntry,
} from "../data/maths/expression";
import {
  REPRESENTATIONS,
  decimalSeparator,
  entryTextOf,
  formatNumeric,
  isNumericValue,
  parseNumericEntry,
} from "../data/maths/numeric";
import { Preview, expressionProblemText } from "./ExpressionEntryStep";


// A numeric cell declares no representation limits, so any exact number is
// accepted; grading is by value (contract §6.3).
const NUMERIC_CELL = { representations: REPRESENTATIONS, allowNegative: true };

const fixedText = (value) =>
  isNumericValue(value) ? formatNumeric(value, decimalSeparator()) : formatExpression(value);

// Read one cell's entry into its canonical value, or { error }.
const readCell = (cell, text, expectation) =>
  cell.entryType === "numeric"
    ? parseNumericEntry(text, NUMERIC_CELL)
    : readExpressionEntry(text, expectation.contract);

const restoredText = (cell, value, expectation) => {
  if (cell.entryType === "numeric") {
    return isNumericValue(value) ? entryTextOf(value, decimalSeparator()) : "";
  }
  return isExpressionValue(value) ? formatExpression(value, { symbols: expectation.symbols }) : "";
};

// Complete the missing cells of a table, or the same cells drawn as an
// input → output flow. Cells are identified by their stable ids: only the
// learner's entries are saved, keyed by cell id, never the authored table.
// A complete, readable table is handed in whether or not it is right; the
// gateway judges it. When it is sent back and the expected values are already
// permitted here, the cells that differ are marked.
const TableCompletionStep = ({ step, saved, verdict, onDone, saving }) => {
  const editable = useMemo(
    () => step.rows.flatMap((row) => row.cells.filter((cell) => cell.editable).map((cell) => ({ cell, row }))),
    [step]
  );
  const columnLabel = (id) => step.columns.find((c) => c.id === id)?.label;
  const cellName = ({ cell, row }, index) =>
    [row.label, columnLabel(cell.column)].filter(Boolean).join(", ") || `Space ${index + 1}`;

  const [texts, setTexts] = useState(() =>
    Object.fromEntries(
      editable.map(({ cell }) => [cell.id, restoredText(cell, saved?.values?.[cell.id], step.expectations[cell.id])])
    )
  );
  const [active, setActive] = useState(editable[0].cell.id);
  const [marked, setMarked] = useState({});
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const lastSent = useRef(null);
  useSentBack(verdict, () => {
    const wrong = lastSent.current ? tableCellsWrong(step, lastSent.current) : [];
    setMarked(Object.fromEntries(wrong.map((id) => [id, "wrong"])));
    setResult("wrong");
  });

  const locked = saving || result === "sent";
  const activeEntry = editable.find(({ cell }) => cell.id === active);
  const activeIndex = editable.indexOf(activeEntry);

  const edit = (next) => {
    setTexts({ ...texts, [active]: next });
    setMarked({ ...marked, [active]: undefined });
    setResult(null);
  };

  const check = () => {
    if (locked) return;
    const values = {};
    const problems = {};
    for (const { cell } of editable) {
      const read = readCell(cell, texts[cell.id], step.expectations[cell.id]);
      if (read.error) problems[cell.id] = read.error === "empty" ? "empty" : "unreadable";
      else values[cell.id] = read.value;
    }
    if (Object.keys(problems).length) {
      setMarked(problems);
      setActive(Object.keys(problems)[0]);
      return;
    }
    const response = { values };
    lastSent.current = response;
    setMarked({});
    setResult("sent");
    onDone(response);
  };

  const markText = { empty: "Empty", unreadable: "Check this", wrong: "Try again" };

  // A render function, not a component: cells keep their identity (and focus)
  // across keystrokes.
  const renderCell = (cell, row) => {
    if (!cell) return <span className={ui.cellBlank} aria-hidden="true" />;
    if (!cell.editable) {
      return <span className={ui.cellFixed}>{fixedText(cell.value)}</span>;
    }
    const index = editable.findIndex((e) => e.cell.id === cell.id);
    const name = cellName({ cell, row }, index);
    const text = texts[cell.id];
    const mark = marked[cell.id];
    return (
      <button
        type="button"
        className={[ui.cellEditable, active === cell.id && ui.cellActive, mark && ui.cellMarked].filter(Boolean).join(" ")}
        aria-pressed={active === cell.id}
        aria-label={`${name}: ${text || "empty"}${mark ? `, ${markText[mark]}` : ""}`}
        onClick={() => setActive(cell.id)}
        disabled={locked}
      >
        <span>{text || "?"}</span>
        {mark && <span className={ui.cellMark}>{markText[mark]}</span>}
      </button>
    );
  };

  const cellIn = (row, columnId) => row.cells.find((c) => c.column === columnId);
  const showRowLabels = step.rows.some((row) => row.label);

  const table = (
    <table className={ui.completionTable}>
      {step.columns.some((c) => c.label) && (
        <thead>
          <tr>
            {showRowLabels && <td />}
            {step.columns.map((column) => (
              <th key={column.id} scope="col">{column.label}</th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {step.rows.map((row) => (
          <tr key={row.id}>
            {showRowLabels && <th scope="row">{row.label}</th>}
            {step.columns.map((column) => (
              <td key={column.id}>
                {renderCell(cellIn(row, column.id), row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  // The same rows, cells and ids, drawn left to right as a flow.
  const flow = (
    <div className={ui.flowRows}>
      {step.columns.some((c) => c.label) && (
        <div className={ui.flowRow} aria-hidden="true">
          {step.columns.map((column, i) => (
            <Fragment key={column.id}>
              {i > 0 && <span className={ui.flowArrowSpace} />}
              <span className={ui.flowHeading}>{column.label}</span>
            </Fragment>
          ))}
        </div>
      )}
      {step.rows.map((row) => (
        <div key={row.id} className={ui.flowRow} role="group" aria-label={row.label || undefined}>
          {step.columns.map((column, i) => (
            <Fragment key={column.id}>
              {i > 0 && (
                <span className={ui.flowArrow} aria-hidden="true">
                  {step.rule && step.columns.length === 2 ? <small>{step.rule.display}</small> : null}
                  {"→"}
                </span>
              )}
              {renderCell(cellIn(row, column.id), row)}
            </Fragment>
          ))}
        </div>
      ))}
    </div>
  );

  const expectation = step.expectations[active];
  const keys =
    activeEntry.cell.entryType === "numeric"
      ? numericKeysFor(NUMERIC_CELL)
      : expressionKeysFor(expectation.contract);
  const activeMark = marked[active];
  const activeProblem =
    activeMark === "unreadable"
      ? activeEntry.cell.entryType === "numeric"
        ? "We can't read that number. Check it and try again."
        : expressionProblemText(
            readExpressionEntry(texts[active], expectation.contract).error || "malformed"
          )
      : activeMark === "empty"
        ? "Fill in this space."
        : "";

  return (
    <>
      <Prompt step={step} />
      {step.rule && <p className={ui.ruleText}>Rule: {step.rule.display}</p>}
      <div className={ui.completion}>
        <div className={ui.completionGrid}>{step.mode === "flow_diagram" ? flow : table}</div>
        <div className={ui.numericAnswer}>
          <AnswerField
            value={texts[active]}
            onChange={edit}
            label={`Answer for ${cellName(activeEntry, activeIndex)}`}
            invalid={Boolean(activeMark)}
            disabled={locked}
          />
          {activeEntry.cell.entryType === "expression" && (
            <Preview text={texts[active]} options={expectation.contract} />
          )}
          <MathKeypad value={texts[active]} onChange={edit} extraKeys={keys} disabled={locked} />
          <p className={ui.mathProblem} role="alert">{activeProblem}</p>
          <div className={ui.toolButtons}>
            {editable.length > 1 && (
              <button
                type="button"
                className={`${ui.button} ${ui.large} ${ui.secondary}`}
                onClick={() => setActive(editable[(activeIndex + 1) % editable.length].cell.id)}
                disabled={locked}
              >
                Next space
              </button>
            )}
            <button type="button" className={`${ui.button} ${ui.large} ${ui.primary}`} onClick={check} disabled={locked}>
              <CheckIcon size={24} />
              {saving ? "Saving…" : "Done"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableCompletionStep;
